import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import {
  formatOdds,
  americanToDecimal,
  decimalToAmerican,
  getOddsFormat,
  setOddsFormat as saveOddsFormat
} from '../utils/oddsUtils';

function ArbitrageCalculator() {
  const location = useLocation();
  const passedData = location.state;

  const [oddsA, setOddsA] = useState('');
  const [oddsB, setOddsB] = useState('');
  const [betA, setBetA] = useState(100);
  const [betB, setBetB] = useState(100);
  const [oddsFormat, setOddsFormat] = useState(getOddsFormat());
  const [showDescription, setShowDescription] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);

  const handleOddsFormatChange = (newFormat) => {
    // Convert current odds to decimal first
    const decimalA = oddsFormat === 'American' ? americanToDecimal(oddsA) : parseFloat(oddsA) || 2.0;
    const decimalB = oddsFormat === 'American' ? americanToDecimal(oddsB) : parseFloat(oddsB) || 2.0;

    // Convert to new format
    if (newFormat === 'American') {
      setOddsA(decimalToAmerican(decimalA));
      setOddsB(decimalToAmerican(decimalB));
    } else {
      setOddsA(decimalA.toFixed(2));
      setOddsB(decimalB.toFixed(2));
    }

    setOddsFormat(newFormat);
    saveOddsFormat(newFormat);
  };

  useEffect(() => {
    if (passedData) {
      // Data comes in as decimal, format for display
      const format = getOddsFormat();
      setOddsA(formatOdds(passedData.oddsA || 2.00, format));
      setOddsB(formatOdds(passedData.oddsB || 2.00, format));
      setBetA(passedData.betA || 100);
      setBetB(passedData.betB || 100);
    } else {
      // Set defaults based on format
      const format = getOddsFormat();
      setOddsA(format === 'American' ? '+100' : '2.00');
      setOddsB(format === 'American' ? '+100' : '2.00');
    }
  }, [passedData]);

  // Convert display odds to decimal for calculations
  const getDecimalOdds = (displayOdds) => {
    if (oddsFormat === 'American') {
      return americanToDecimal(displayOdds);
    }
    return parseFloat(displayOdds) || 2.0;
  };

  const decimalOddsA = getDecimalOdds(oddsA);
  const decimalOddsB = getDecimalOdds(oddsB);

  const payoutA = Number(betA) * decimalOddsA;
  const profitA = payoutA - Number(betA) - Number(betB);
  const roiA = (profitA / (Number(betA) + Number(betB))) * 100;

  const payoutB = Number(betB) * decimalOddsB;
  const profitB = payoutB - Number(betA) - Number(betB);
  const roiB = (profitB / (Number(betA) + Number(betB))) * 100;

  const getBorderColor = (value) => {
    if (value > -0.01 && value < 0.01) return '#4a5568';
    return value > 0 ? '#10b981' : '#f87171';
  };

  const getTextColor = (value) => {
    if (value > -0.01 && value < 0.01) return '#a0aec0';
    return value > 0 ? '#10b981' : '#f87171';
  };

  return (
    <div style={{
      backgroundColor: '#0a0e1a',
      minHeight: '100vh',
      padding: '40px 60px'
    }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h1 style={{
            fontSize: '36px',
            fontWeight: '800',
            margin: 0,
            color: '#ffffff',
            letterSpacing: '-0.5px'
          }}>
            Arbitrage Calculator
          </h1>

          {/* Odds Format Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{
              fontSize: '14px',
              fontWeight: '600',
              color: oddsFormat === 'American' ? '#ffffff' : '#64748b',
              transition: 'color 0.2s'
            }}>
              American
            </span>
            <div
              onClick={() => handleOddsFormatChange(oddsFormat === 'American' ? 'Decimal' : 'American')}
              style={{
                width: '48px',
                height: '26px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #2d3748',
                borderRadius: '13px',
                cursor: 'pointer',
                position: 'relative',
                transition: 'all 0.2s'
              }}
            >
              <div style={{
                width: '20px',
                height: '20px',
                backgroundColor: '#00d9ff',
                borderRadius: '50%',
                position: 'absolute',
                top: '2px',
                left: oddsFormat === 'American' ? '3px' : '23px',
                transition: 'left 0.2s ease'
              }} />
            </div>
            <span style={{
              fontSize: '14px',
              fontWeight: '600',
              color: oddsFormat === 'Decimal' ? '#ffffff' : '#64748b',
              transition: 'color 0.2s'
            }}>
              Decimal
            </span>
          </div>
        </div>

        {/* Description Section */}
        <div style={{
          backgroundColor: '#1a1f2e',
          border: '1px solid #2d3748',
          borderRadius: '8px',
          padding: '14px 20px',
          marginBottom: '10px'
        }}>
          <div
            onClick={() => setShowDescription(!showDescription)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            <h3 style={{
              margin: 0,
              fontSize: '15px',
              fontWeight: '600',
              color: '#ffffff'
            }}>
              Description
            </h3>
            <ChevronDown
              size={20}
              color="#a0aec0"
              style={{
                transition: 'transform 0.2s ease',
                transform: showDescription ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            />
          </div>
          {showDescription && (
            <div style={{
              fontSize: '15px',
              lineHeight: '1.7',
              color: '#a0aec0',
              margin: '15px 0 0 0'
            }}>
              <p style={{ margin: '0 0 10px 0' }}>
                Arbitrage betting locks in profit by betting on all outcomes across different sportsbooks.
              </p>
              <p style={{ margin: '0 0 10px 0' }}>
                Use this calculator to test opportunities you find on your own, explore different bet sizing scenarios, or verify the math before placing bets.
              </p>
              <p style={{ margin: 0 }}>
                Both outcome boxes should show positive profit (green) for a true arbitrage opportunity.
              </p>
            </div>
          )}
        </div>

        {/* How to Use Section */}
        <div style={{
          backgroundColor: '#1a1f2e',
          border: '1px solid #2d3748',
          borderRadius: '8px',
          padding: '14px 20px',
          marginBottom: '20px'
        }}>
          <div
            onClick={() => setShowHowTo(!showHowTo)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            <h3 style={{
              margin: 0,
              fontSize: '15px',
              fontWeight: '600',
              color: '#ffffff'
            }}>
              How to Use
            </h3>
            <ChevronDown
              size={20}
              color="#a0aec0"
              style={{
                transition: 'transform 0.2s ease',
                transform: showHowTo ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            />
          </div>
          {showHowTo && (
            <div style={{
              fontSize: '15px',
              lineHeight: '1.7',
              color: '#a0aec0',
              margin: '15px 0 0 0'
            }}>
              <ol style={{ margin: 0, paddingLeft: '20px' }}>
                <li style={{ marginBottom: '8px' }}>Enter the odds for each side from different sportsbooks</li>
                <li style={{ marginBottom: '8px' }}>Adjust bet amounts to balance profit across outcomes</li>
                <li style={{ marginBottom: '8px' }}>Look for positive profit in both result boxes</li>
                <li>A true arbitrage shows green on both sides</li>
              </ol>
            </div>
          )}
        </div>

        {/* Calculator */}
        <div style={{
          backgroundColor: '#1a1f2e',
          border: '1px solid #2d3748',
          borderRadius: '12px',
          padding: '30px'
        }}>
          {/* Two sides */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
            {/* Side A */}
            <div style={{
              backgroundColor: '#0f1419',
              padding: '24px',
              borderRadius: '10px',
              border: '1px solid #2d3748'
            }}>
              <h3 style={{
                color: '#ffffff',
                marginTop: 0,
                marginBottom: '20px',
                fontSize: '16px',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Side A
              </h3>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#a0aec0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Odds ({oddsFormat})
                </label>
                <input
                  type="text"
                  value={oddsA}
                  onChange={(e) => setOddsA(e.target.value)}
                  placeholder={oddsFormat === 'American' ? '+100' : '2.00'}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #2d3748',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '16px',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#00d9ff'}
                  onBlur={(e) => e.target.style.borderColor = '#2d3748'}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#a0aec0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Bet Amount
                </label>
                <input
                  type="number"
                  value={betA}
                  onChange={(e) => setBetA(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #2d3748',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '16px',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#00d9ff'}
                  onBlur={(e) => e.target.style.borderColor = '#2d3748'}
                />
              </div>
            </div>

            {/* Side B */}
            <div style={{
              backgroundColor: '#0f1419',
              padding: '24px',
              borderRadius: '10px',
              border: '1px solid #2d3748'
            }}>
              <h3 style={{
                color: '#ffffff',
                marginTop: 0,
                marginBottom: '20px',
                fontSize: '16px',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Side B
              </h3>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#a0aec0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Odds ({oddsFormat})
                </label>
                <input
                  type="text"
                  value={oddsB}
                  onChange={(e) => setOddsB(e.target.value)}
                  placeholder={oddsFormat === 'American' ? '+100' : '2.00'}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #2d3748',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '16px',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#00d9ff'}
                  onBlur={(e) => e.target.style.borderColor = '#2d3748'}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#a0aec0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Bet Amount
                </label>
                <input
                  type="number"
                  value={betB}
                  onChange={(e) => setBetB(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #2d3748',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '16px',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#00d9ff'}
                  onBlur={(e) => e.target.style.borderColor = '#2d3748'}
                />
              </div>
            </div>
          </div>

          {/* Results */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            {/* If Side A Wins */}
            <div style={{
              backgroundColor: '#0f1419',
              padding: '24px',
              borderRadius: '10px',
              border: `3px solid ${getBorderColor(profitA)}`,
              color: '#ffffff'
            }}>
              <div style={{
                textAlign: 'center',
                marginBottom: '20px',
                fontSize: '14px',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                If Side A Wins
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '12px',
                fontSize: '14px'
              }}>
                <span style={{ color: '#a0aec0' }}>Payout:</span>
                <span style={{ fontWeight: '700', fontFamily: 'monospace' }}>${payoutA.toFixed(2)}</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '12px',
                fontSize: '14px'
              }}>
                <span style={{ color: '#a0aec0' }}>Profit:</span>
                <span style={{
                  fontWeight: '700',
                  fontFamily: 'monospace',
                  color: getTextColor(profitA)
                }}>
                  {profitA > 0 ? '+' : ''}${profitA.toFixed(2)}
                </span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '14px'
              }}>
                <span style={{ color: '#a0aec0' }}>ROI:</span>
                <span style={{
                  fontWeight: '700',
                  fontFamily: 'monospace',
                  color: getTextColor(roiA)
                }}>
                  {roiA > 0 ? '+' : ''}{roiA.toFixed(2)}%
                </span>
              </div>
            </div>

            {/* If Side B Wins */}
            <div style={{
              backgroundColor: '#0f1419',
              padding: '24px',
              borderRadius: '10px',
              border: `3px solid ${getBorderColor(profitB)}`,
              color: '#ffffff'
            }}>
              <div style={{
                textAlign: 'center',
                marginBottom: '20px',
                fontSize: '14px',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                If Side B Wins
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '12px',
                fontSize: '14px'
              }}>
                <span style={{ color: '#a0aec0' }}>Payout:</span>
                <span style={{ fontWeight: '700', fontFamily: 'monospace' }}>${payoutB.toFixed(2)}</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '12px',
                fontSize: '14px'
              }}>
                <span style={{ color: '#a0aec0' }}>Profit:</span>
                <span style={{
                  fontWeight: '700',
                  fontFamily: 'monospace',
                  color: getTextColor(profitB)
                }}>
                  {profitB > 0 ? '+' : ''}${profitB.toFixed(2)}
                </span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '14px'
              }}>
                <span style={{ color: '#a0aec0' }}>ROI:</span>
                <span style={{
                  fontWeight: '700',
                  fontFamily: 'monospace',
                  color: getTextColor(roiB)
                }}>
                  {roiB > 0 ? '+' : ''}{roiB.toFixed(2)}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ArbitrageCalculator;
